import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { ChatBubble } from './ChatBubble'
import { ChatbotContext } from '../context/ChatbotProvider'
import { useChatService } from '../services/chatService'
import { Spinner } from 'reactstrap'
import { CHAT_MSG_PER_PAGE } from '../utils/constants'
// @ts-ignore
import { Button } from 'dsm-react-lib'
import { IconArrowNarrowDown } from '@tabler/icons-react'

const ChatMessages = ({ totalMsgCount }: any) => {
  const { messages, isStreaming } = useContext(ChatbotContext)
  const chatContainerRef: any = useRef<HTMLDivElement>(null)
  const lastUserMsgRef = useRef<HTMLDivElement>(null)
  const spacerRef = useRef<HTMLDivElement>(null)
  const fadeSpacerRef = useRef<HTMLDivElement>(null)
  const prevLastUserMsgIndex = useRef(-1)
  const wasStreamingRef = useRef(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [hasMore, setHasMore] = useState(totalMsgCount > messages.length)
  const { isFetchingChatMessages } = useChatService()
  const [isRegenerating, setIsRegenerating] = useState(false)
  const [showFab, setShowFab] = useState(false)

  // Find the index of the latest user message
  const lastUserMsgIndex = messages.reduce(
    (last: number, msg: any, i: number) => (msg.role === 'user' ? i : last),
    -1
  )

  // When a new user message appears, smooth-scroll it to the top of the viewport
  useLayoutEffect(() => {
    if (lastUserMsgIndex > prevLastUserMsgIndex.current) {
      prevLastUserMsgIndex.current = lastUserMsgIndex
      setTimeout(() => {
        const container = chatContainerRef.current
        const msgEl = lastUserMsgRef.current
        const spacerEl = spacerRef.current
        if (container && msgEl && spacerEl) {
          const rawTarget = msgEl.offsetTop
          if (rawTarget <= 0) {
            // Message is already at/near the top — no scroll or spacer needed
            spacerEl.style.minHeight = '0px'
            return
          }
          // Temporarily collapse spacer to measure natural content height
          spacerEl.style.minHeight = '0px'
          // Don't use container.scrollHeight — when content is smaller than the
          // viewport it returns clientHeight, which under-calculates the spacer.
          const containerRect = container.getBoundingClientRect()
          const spacerRect = spacerEl.getBoundingClientRect()
          const contentHeight = container.scrollTop + (spacerRect.top - containerRect.top)
          const clientHeight = container.clientHeight
          // Only add enough spacer so the user message can reach the top
          const needed = Math.max(0, rawTarget + clientHeight - contentHeight)
          spacerEl.style.minHeight = `${needed}px`
          container.scrollTo({ top: rawTarget, behavior: 'smooth' })
        }
      }, 100)
    }
  }, [lastUserMsgIndex])

  // During streaming, dynamically shrink the spacer as content grows
  useEffect(() => {
    if (!isStreaming) return
    const container = chatContainerRef.current
    const spacerEl = spacerRef.current
    const msgEl = lastUserMsgRef.current
    if (!container || !spacerEl || !msgEl) return

    const spacerHeight = spacerEl.offsetHeight
    const { scrollHeight, clientHeight } = container
    const realContentHeight = scrollHeight - spacerHeight
    const rawTarget = Math.max(0, msgEl.offsetTop)
    const needed = Math.max(0, rawTarget + clientHeight - realContentHeight)
    spacerEl.style.minHeight = `${needed}px`
  }, [messages, isStreaming])

  // Show FAB when streamed content overflows below the viewport
  useEffect(() => {
    if (isStreaming && chatContainerRef.current) {
      const { scrollTop, clientHeight, scrollHeight } = chatContainerRef.current
      const spacerHeight =
        (spacerRef.current?.offsetHeight ?? 0) + (fadeSpacerRef.current?.offsetHeight ?? 0)
      const isOverflowing = scrollTop + clientHeight < scrollHeight - spacerHeight - 100
      if (isOverflowing && !showFab) setShowFab(true)
    }
  }, [messages, isStreaming])

  // After streaming ends, collapse the spacer only if it won't cause scrollTop to clamp
  useEffect(() => {
    if (isStreaming) {
      wasStreamingRef.current = true
    } else if (wasStreamingRef.current) {
      wasStreamingRef.current = false
      const container = chatContainerRef.current
      const spacerEl = spacerRef.current
      if (container && spacerEl) {
        const spacerHeight = spacerEl.offsetHeight
        if (spacerHeight === 0) return
        const { scrollTop, clientHeight, scrollHeight } = container
        // Only collapse if real content (without spacer) still fills the viewport at the current scroll
        // position. Otherwise the browser would clamp scrollTop, causing a visible jump.
        if (scrollHeight - spacerHeight >= scrollTop + clientHeight) {
          spacerEl.style.minHeight = '0px'
        }
      }
    }
  }, [isStreaming])

  useEffect(() => {
    if (!isStreaming && isRegenerating) {
      setIsRegenerating(false)
    }
  }, [isStreaming])

  const handleScroll = () => {
    if (chatContainerRef.current) {
      const { scrollTop, clientHeight, scrollHeight } = chatContainerRef.current
      const currentMsgCount = messages?.length || 0
      if (
        scrollTop === 0 &&
        hasMore &&
        !isFetchingChatMessages &&
        !isStreaming &&
        currentMsgCount >= CHAT_MSG_PER_PAGE
      ) {
        setPageNumber((prevPageNumber) => prevPageNumber + 1)
      }
      const spacerHeight =
        (spacerRef.current?.offsetHeight ?? 0) + (fadeSpacerRef.current?.offsetHeight ?? 0)
      const isUserScrolling = scrollTop + clientHeight < scrollHeight - spacerHeight - 100
      setShowFab(isUserScrolling)
    }
  }

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      if (isStreaming && spacerRef.current) {
        // scroll to where the actual content ends
        const containerRect = chatContainerRef.current.getBoundingClientRect()
        const spacerRect = spacerRef.current.getBoundingClientRect()
        const contentBottom =
          chatContainerRef.current.scrollTop + (spacerRect.top - containerRect.top)
        // Scroll so the content bottom aligns with the viewport bottom
        chatContainerRef.current.scrollTo({
          top: contentBottom,
          behavior: 'smooth'
        })
      } else {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight
      }
      setShowFab(false) // Hide the FAB after scrolling down
    }
  }

  return (
    <div
      ref={chatContainerRef}
      onScroll={handleScroll}
      className="flex flex-col h-full w-full overflow-auto scrollbar-none">
      {isFetchingChatMessages && (
        <div className="flex justify-center items-center p-4 gap-4 min-h-[80px]">
          <Spinner color="primary" />
          Loading...
        </div>
      )}
      {messages?.map((msg: any, index: number) => {
        return (
          <div key={index} ref={index === lastUserMsgIndex ? lastUserMsgRef : undefined}>
            <ChatBubble message={msg} messageIndex={index} setIsRegenerating={setIsRegenerating} />
          </div>
        )
      })}
      {/* Spacer ensures enough scroll room so the latest user message can always reach the top */}
      <div ref={spacerRef} className="shrink-0" />
      {/* Extra space so the fade overlay doesn't clip the last message's action icons */}
      <div ref={fadeSpacerRef} className="chat-fade-spacer" />
      {showFab && (
        <div className="self-end sticky bottom-2.5 right-4 z-20">
          <div className="[&>button]:!border-none [&>button]:!border-0 [&>button]:!outline-none">
            <Button
              buttonType="rounded"
              variant="outline"
              color="var(--color-primary)"
              label="Search"
              iconOnly={true}
              icon={<IconArrowNarrowDown size={18} />}
              onClick={scrollToBottom}
              className={`theme-bg-fab chat-primary`}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default ChatMessages
